var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Checkbox as AntdCheckbox, Input as AntdInput, InputNumber as AntdInputNumber, Radio as AntdRadio, Segmented as AntdSegmented, Select as AntdSelect, Switch as AntdSwitch, TimePicker as AntdTimePicker, Tooltip as AntdTooltip, TreeSelect as AntdTreeSelect, Typography } from "antd";
import { CheckOutlined, CloseOutlined, EditOutlined } from "@ant-design/icons";
import { useCallback, useEffect, useMemo, useState } from "react";
import { green6, red6 } from "../../assets/variables";
import "./Input.scss";
import { debounce, isNumber } from "lodash";
import Label from "../Label/Label";
import dayjs from "dayjs";
/**
 * HOC allowing you to wrap any input component with a label and tooltip,
 * but still define your own input component. Conditionally renders a label
 * and tooltip around its children.
 */
export const LabelWrapper = ({ label, tooltip, wrapperStyle, wrapperClassName, children, flow = "row" }) => {
    if (label) {
        return (_jsxs("div", { className: ["input-wrapper", flow, wrapperClassName].join(" "), style: Object.assign({}, wrapperStyle), children: [_jsx(Label, { title: label, tooltip: tooltip }), tooltip ? children : _jsx(AntdTooltip, { children: children })] }));
    }
    if (tooltip) {
        return (_jsx(AntdTooltip, { placement: "topLeft", title: tooltip, children: children }));
    }
    return (_jsx("div", { className: wrapperClassName, style: wrapperStyle, children: children }));
};
/** Default text Input component with label, tooltip, and debounced onChange. */
export const Input = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle, debounceTime = 1000, onChange = () => { } } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle", "debounceTime", "onChange"]);
    const [localValue, setLocalValue] = useState(args.value);
    useEffect(() => {
        setLocalValue(args.value);
    }, [args.value]);
    const onChangeDebounce = useCallback(debounce(onChange, debounceTime), [
        onChange,
        debounceTime
    ]);
    const handleChange = (value) => {
        setLocalValue(value);
        if (debounceTime) {
            onChangeDebounce(value);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
    };
    const onBlur = () => {
        onChangeDebounce.cancel();
        onChange === null || onChange === void 0 ? void 0 : onChange((localValue === null || localValue === void 0 ? void 0 : localValue.toString()) || "");
    };
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdInput, Object.assign({}, args, { value: localValue, onChange: (e) => handleChange(e.target.value), onBlur: onBlur })) }));
};
/** Input for inline editing with confirm/cancel actions. */
export const EditableTextInput = (_a) => {
    var { label, tooltip, onConfirm } = _a, args = __rest(_a, ["label", "tooltip", "onConfirm"]);
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(args.value);
    useEffect(() => {
        setLocalValue(args.value);
    }, [args.value]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: args.flow, children: editing ? (_jsxs("div", { className: "te-flex", children: [_jsx(AntdInput, Object.assign({}, args, { value: localValue, onChange: (e) => setLocalValue(e.target.value), onPressEnter: () => {
                        setEditing(false);
                        onConfirm(localValue);
                    } })), _jsxs("div", { className: "te-flex te-pl-3", children: [_jsx(CheckOutlined, { className: `icon-button te-text-sm ${!args.disabled ? "te-cursor-pointer" : "!te-cursor-not-allowed"}`, onClick: () => {
                                setEditing(false);
                                onConfirm(localValue);
                            }, style: { color: green6 } }), _jsx(CloseOutlined, { className: "icon-button close-icon te-text-sm te-cursor-pointer te-pl-3", onClick: () => {
                                setEditing(false);
                                setLocalValue(args.value);
                            }, style: { color: red6 } })] })] })) : (_jsxs("div", { className: "te-flex", children: [_jsx(Typography.Text, { style: { marginLeft: "8px" }, ellipsis: true, children: `${args.value}` }), _jsx(EditOutlined, { className: "icon-button te-text-sm te-cursor-pointer te-pl-3", onClick: () => setEditing(true) })] })) }));
};
/** Editable number input with inline confirm/cancel and auto-fix for out-of-bound values. */
export const EditableNumberInput = (_a) => {
    var { label, tooltip, value, onConfirm, autoFix, max, min } = _a, rest = __rest(_a, ["label", "tooltip", "value", "onConfirm", "autoFix", "max", "min"]);
    const [editing, setEditing] = useState(false);
    const [localValue, setLocalValue] = useState(value);
    useEffect(() => {
        setLocalValue(value);
    }, [value]);
    const isValid = useMemo(() => {
        const { required } = rest;
        if ((required && localValue === null) || localValue === undefined) {
            return false;
        }
        if (min !== undefined && Number(localValue) < Number(min)) {
            return false;
        }
        if (max !== undefined && Number(localValue) > Number(max)) {
            return false;
        }
        return true;
    }, [localValue]);
    const onSubmit = () => {
        if (!isValid) {
            if (autoFix) {
                setEditing(false);
                let fixedValue;
                if (localValue === null || localValue === undefined)
                    fixedValue = min || 0;
                if (min !== undefined && Number(localValue) < Number(min)) {
                    fixedValue = min;
                }
                else if (max !== undefined && Number(localValue) > Number(max)) {
                    fixedValue = max;
                }
                setEditing(false);
                setLocalValue(fixedValue);
                onConfirm(fixedValue);
            }
            return;
        }
        setEditing(false);
        onConfirm(localValue === undefined ? localValue : Number(localValue));
    };
    const onCancel = () => {
        setEditing(false);
        setLocalValue(value);
    };
    const renderedValue = useMemo(() => {
        if (editing)
            return (_jsx(AntdInputNumber, Object.assign({ status: isValid ? "" : "error" }, rest, { autoFocus: true, value: localValue, onChange: (val) => {
                    setLocalValue(val);
                }, onPressEnter: onSubmit })));
        return _jsx(Typography.Text, { children: localValue });
    }, [editing, localValue, isValid]);
    const buttons = useMemo(() => {
        if (!editing)
            return (_jsx(EditOutlined, { className: "te-text-sm te-cursor-pointer", onClick: () => setEditing(true) }));
        return (_jsxs(_Fragment, { children: [_jsx(CheckOutlined, { style: { color: green6 }, className: `te-text-sm ${isValid ? "te-cursor-pointer" : "!te-cursor-not-allowed"}`, onClick: onSubmit }), _jsx(CloseOutlined, { style: { color: red6 }, className: "te-text-sm te-cursor-pointer", onClick: onCancel })] }));
    }, [editing, localValue, isValid]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: rest.flow, children: _jsxs("div", { className: "te-flex te-items-center te-gap-x-2 min-h-[30px]", children: [renderedValue, buttons] }) }));
};
/** Multi-line text input with label and tooltip. */
export const TextArea = (_a) => {
    var { label, tooltip, onChange = () => { }, debounceTime = 1000, wrapperStyle, wrapperClassName } = _a, args = __rest(_a, ["label", "tooltip", "onChange", "debounceTime", "wrapperStyle", "wrapperClassName"]);
    const [localValue, setLocalValue] = useState(args.value);
    useEffect(() => {
        setLocalValue(args.value);
    }, [args.value]);
    const onChangeDebounce = useCallback(debounce(onChange, debounceTime), [
        onChange,
        debounceTime
    ]);
    const handleChange = (value) => {
        setLocalValue(value);
        if (debounceTime) {
            onChangeDebounce(value);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
    };
    const onBlur = () => {
        onChangeDebounce.cancel();
        onChange === null || onChange === void 0 ? void 0 : onChange((localValue === null || localValue === void 0 ? void 0 : localValue.toString()) || "");
    };
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: args.flow, wrapperStyle: wrapperStyle, wrapperClassName: wrapperClassName, children: _jsx(AntdInput.TextArea, Object.assign({}, args, { value: localValue, onChange: (e) => handleChange(e.target.value), onBlur: onBlur })) }));
};
/** Display static text with label and tooltip. */
export const PureText = (_a) => {
    var { label, tooltip, className, value } = _a, rest = __rest(_a, ["label", "tooltip", "className", "value"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: rest.flow, children: _jsx(Typography.Text, { className: className, children: value }) }));
};
/** Select a single option with label and tooltip. */
export const Select = (_a) => {
    var _b;
    var { label, tooltip, flow, wrapperStyle, wrapperClassName } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperStyle", "wrapperClassName"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperStyle: wrapperStyle, wrapperClassName: wrapperClassName, children: _jsx(AntdSelect, Object.assign({}, args, { popupMatchSelectWidth: false, allowClear: true, placeholder: "Select", children: (_b = args.options) === null || _b === void 0 ? void 0 : _b.map((opt, i) => (_jsxs(AntdSelect.Option, { value: opt.value, children: [" ", opt.label] }, i))) })) }));
};
/** Hierarchical selection with label and tooltip. */
export function TreeSelect(_a) {
    var { label, tooltip, flow, wrapperStyle, wrapperClassName, className = "tree-select-item" } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperStyle", "wrapperClassName", "className"]);
    const getPopupContainer = useCallback(() => document.querySelector(`.${className}`) || document.createElement("div"), [className]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperStyle: wrapperStyle, wrapperClassName: wrapperClassName, children: _jsx(AntdTreeSelect, Object.assign({ placeholder: "Select", showSearch: true, popupMatchSelectWidth: false, className: className, allowClear: true, filterTreeNode: filterTreeNode, getPopupContainer: getPopupContainer }, args)) }));
}
const filterTreeNode = (input, { label, title }) => {
    const value = typeof title === "string" ? title : label;
    if (typeof value !== "string") {
        return false;
    }
    return value.toLowerCase().includes(input.toLowerCase());
};
/** Toggle a boolean value with label and tooltip. */
export const Switch = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle, size } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle", "size"]);
    const switchSize = useSupportedSwitchOrPaginationSize(size);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdSwitch, Object.assign({ style: { justifySelf: "start", alignSelf: "center" } }, args, { size: switchSize })) }));
};
/**
 * AntD Switch and Pagination only support `small` and `default` —
 * not the full `SizeType`.
 * `small` is preserved; `middle` and `large` map to `default`.
 */
export const useSupportedSwitchOrPaginationSize = (size) => {
    return useMemo(() => (size === "small" ? "small" : "default"), [size]);
};
/** Boolean selection with label and tooltip. */
export const Checkbox = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdCheckbox, Object.assign({}, args)) }));
};
/** Radio component for single option selection with label and tooltip. */
const Radio = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdRadio, Object.assign({}, args)) }));
};
/** Numeric entry with label and tooltip. */
export const InputNumber = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdInputNumber, Object.assign({}, args, { onStep: (newVal, info) => {
                const stepSize = (args.step ? args.step : 1);
                if (info.type === "up") {
                    if (args.onChange) {
                        args.onChange(Math.floor(newVal / stepSize) * stepSize);
                    }
                    return;
                }
                if (args.onChange) {
                    args.onChange(Math.ceil(newVal / stepSize) * stepSize);
                }
            } })) }));
};
/** Enter a minimum and maximum value with label and tooltip. */
const InputNumberMinMax = ({ label, tooltip, flow, wrapperClassName, wrapperStyle, debounceTime = 1000, value, onChange, disabled }) => {
    const onChangeDebounce = useCallback(debounce(onChange, debounceTime), [
        onChange,
        debounceTime
    ]);
    const handleChange = (value) => {
        if (debounceTime) {
            onChangeDebounce(value);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
    };
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsxs("div", { className: "te-min-max-container", "data-testid": "min-max-input", children: [_jsx(InputNumber, { placeholder: "Min", value: value[0], disabled: disabled, onChange: (newVal) => {
                        const newMinMax = [newVal === null || newVal === void 0 ? void 0 : newVal.valueOf(), value[1]];
                        handleChange(newMinMax);
                    } }), _jsx(PureText, { value: ":" }), _jsx(InputNumber, { placeholder: "Max", value: value[1], disabled: disabled, onChange: (newVal) => {
                        const newMinMax = [value[0], newVal === null || newVal === void 0 ? void 0 : newVal.valueOf()];
                        handleChange(newMinMax);
                    } })] }) }));
};
/** Enter a password */
export const Password = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle, onChange = () => { }, debounceTime = 1000 } = _a, args = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle", "onChange", "debounceTime"]);
    const [localValue, setLocalValue] = useState(args.value);
    useEffect(() => {
        setLocalValue(args.value);
    }, [args.value]);
    const onChangeDebounce = useCallback(debounce(onChange, debounceTime), [
        onChange,
        debounceTime
    ]);
    const handleChange = (value) => {
        setLocalValue(value);
        if (debounceTime) {
            onChangeDebounce(value);
        }
        else {
            onChange === null || onChange === void 0 ? void 0 : onChange(value);
        }
    };
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdInput.Password, Object.assign({}, args, { value: localValue, type: "password", onChange: (e) => handleChange(e.target.value) })) }));
};
/** Component used to input hours and minutes. Can be used within a
 form, in combination with a label, with or without a tooltip or on
 its own. When defining a label, the container div will utilize a
 grid layout where the first column adjusts it width to the parent
 container, and the second column's width is 240px.
*/
export const HoursMinuteInput = ({ label, tooltip, wrapperClassName, wrapperStyle, style, value, className, flow, size, onChange, readOnly, disabled, minuteStep = 1 }) => {
    const hours = value[0];
    const mins = value[1];
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsxs("div", { style: Object.assign({ display: "flex", gap: "8px", alignItems: "center" }, style), className: className, children: [_jsx(InputNumber, { "data-testid": "hours-minute-input__hours", className: "double-digit-input", size: size, value: hours, onChange: (val) => {
                        const valNumber = isNumber(val) ? val : 0;
                        onChange([valNumber, mins || 0]);
                    }, readOnly: readOnly, disabled: disabled, placeholder: "HH", min: 0, formatter: (value) => value && Number(value) < 10 ? `0${value}` : `${value}` }), _jsx(Typography.Text, { children: ":" }), _jsx(InputNumber, { "data-testid": "hours-minute-input__minutes", className: "double-digit-input", size: size, value: mins, onChange: (val) => {
                        const valNumber = isNumber(val) ? val : 0;
                        onChange([hours || 0, valNumber]);
                    }, readOnly: readOnly, disabled: disabled, min: 0, max: 59, placeholder: "MM", step: minuteStep, formatter: (value) => value && Number(value) < 10 ? `0${value}` : `${value}` })] }) }));
};
/** Select an option from a group with label and tooltip. */
export const RadioGroup = ({ label, tooltip, size, value, onChange, disabled, buttonStyle, options, flow }) => {
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, wrapperStyle: {
            gridTemplateColumns: buttonStyle === "solid" ? "100px auto" : "auto 240px",
            alignItems: "start"
        }, flow: flow, children: _jsx(AntdRadio.Group, { size: size, value: value, onChange: (e) => onChange && onChange(e.target.value), disabled: disabled, buttonStyle: buttonStyle, children: options === null || options === void 0 ? void 0 : options.map((opt, i) => buttonStyle === "solid" ? (_jsx(AntdRadio.Button, { value: opt.value, children: opt.label }, i)) : (_jsx(Radio, { value: opt, children: opt.label }, i))) }) }));
};
/** Switch between options with label and tooltip. */
export const Segmented = ({ label, tooltip, size, value, onChange, disabled, options, flow, block = true, wrapperClassName, wrapperStyle }) => {
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdSegmented, { size: size, value: value, onChange: (e) => onChange && onChange(e), disabled: disabled, options: options, block: block }) }));
};
/** Select a time with a certain format with label and tooltip. */
export const TimePicker = (_a) => {
    var { label, tooltip, flow, wrapperClassName, wrapperStyle, value, onChange, hideFooter } = _a, rest = __rest(_a, ["label", "tooltip", "flow", "wrapperClassName", "wrapperStyle", "value", "onChange", "hideFooter"]);
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsx(AntdTimePicker, Object.assign({ style: { width: "200px" }, onChange: (value, dateStr) => {
                if (onChange) {
                    onChange(value || dayjs(), dateStr);
                }
            }, value: value }, rest, { popupClassName: hideFooter ? "te-hide-footer" : "" })) }));
};
/** KeyValue component for displaying a key-value pair with label and tooltip. */
export const KeyValue = ({ label, tooltip, flow, wrapperClassName, wrapperStyle, keyComponent, valueComponent }) => {
    return (_jsx(LabelWrapper, { label: label, tooltip: tooltip, flow: flow, wrapperClassName: wrapperClassName, wrapperStyle: wrapperStyle, children: _jsxs("div", { className: "te-key-value-container", "data-testid": "key-value-input", children: [_jsx("div", { className: "te-key-value-item", children: keyComponent }), _jsx(Typography.Text, { children: ":" }), _jsx("div", { className: "te-key-value-item", children: valueComponent })] }) }));
};
export default {
    Input,
    EditableTextInput,
    TextArea,
    PureText,
    TimePicker,
    Select,
    Switch,
    Checkbox,
    Radio,
    InputNumber,
    Password,
    HoursMinuteInput,
    RadioGroup,
    LabelWrapper,
    EditableNumberInput,
    KeyValue,
    InputNumberMinMax,
    Segmented,
    TreeSelect
};
